import React, { useEffect, useState } from 'react';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { getAchievement } from '@/services/achievement.service';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { getMainPart, getSpecificPart, Achievement, SpecificType, UpsertAchievement, UpsertSpecificType } from '@/models/achievement.model';
import MainAchievementForm from './achievement-main-form';
import PageHeader from '@/components/PageHeader';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect} >
      <Nav.Item eventKey="achievement-detail">Achievement Detail</Nav.Item>
    </Nav>
  );
};

const Page = () => {
  
  const requestedTab = useLocation()?.state?.tab;
  const initValue : UpsertAchievement&UpsertSpecificType = useLocation()?.state?.initValue;

  const [activeTab, setActiveTab] = useState('achievement-detail');
  const [formObject, setFormObject] = useState<Achievement | undefined>(undefined);
  const [specificFormObject, setSpecificFormObject] = useState<SpecificType | undefined>(undefined);
  const [loading, setLoading] = useState(0);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;
  
  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);

  const fetchData = async () => {
    if (id) {
      setLoading(pre => ++pre);
      try {
        const result: BaseResponseModel<Achievement & SpecificType> = await getAchievement(id);
        if (result.success) {
          setFormObject(getMainPart(result.result));
          setSpecificFormObject(getSpecificPart(result.result));
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching achievement: ${errorModel?.errorMessage}`);
        nav('/achievements');
      }
      finally {
        setLoading(pre => --pre);
      }
    }
  };

  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit ? `Edit achievement: ${formObject?.name}` : 'New achievement'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href='/achievements'>Achievements</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" 
        active={activeTab} 
        onSelect={key => setActiveTab(key)} 
        newMode={pageState == PageState.New} 
      />
      {
        !!loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'achievement-detail' && <MainAchievementForm 
            formObject={formObject} 
            specificFormObject={specificFormObject} 
            pageState={pageState}
            initValue={initValue}
          />}
        </>
      }    
    </Panel>
  );
};

export default Page;
